import { useRead, ReadState, isVideos } from "@/components/LoadingStates";

import LiveDemoBadge, { isLiveCreator } from "@/components/LiveDemoBadge";
import MediaImage from "@/components/MediaImage";

import { useParams, Link } from "react-router-dom";

import VideoCard from "@/components/VideoCard";
import PlatformBadge from "@/components/PlatformBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import CompareCreatorPicker from "@/components/CompareCreatorPicker";
import { formatNumber } from "@/lib/format";
import { ArrowLeft, GitCompare } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, ReferenceLine } from "recharts";

const CHART_LIMIT = 20;

export default function CreatorDetail() {
  const { id } = useParams();
  const detailRead = useRead(`/creators/${id}`, d => d && d.creator && typeof d.creator.name === "string" && isVideos(d.videos));
  const data = detailRead.data;

  if (detailRead.status !== "success") return <div data-testid="creator-detail-page"><Link to="/creators" className="text-sm text-primary">Retour à la liste</Link><ReadState read={detailRead} scope="creator-detail" /></div>;
  const { creator, videos } = data;
  // The API caps videos at 500 and sends the full count as total; ignore a total that cannot describe this list.
  const profileTotal = Number.isInteger(data.total) && data.total >= videos.length ? data.total : null;
  const chartCount = Math.min(videos.length, CHART_LIMIT);

  const chartData = [...videos].slice(0, CHART_LIMIT).reverse().map((v, i) => ({
    idx: i + 1,
    views: v.views,
    is_outlier: v.is_outlier,
    title: v.title,
  }));

  const outliers = videos.filter((v) => v.is_outlier);

  return (
    <div className="space-y-6" data-testid="creator-detail-page">
      <Link to="/creators" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-creators">
        <ArrowLeft className="h-4 w-4" /> Back to creators
      </Link>

      <Card className="p-6">
        <div className="flex flex-wrap items-center gap-4">
          <MediaImage src={creator.avatar} alt="" name={creator.name} kind="avatar" demo={!isLiveCreator(creator)} className="h-20 w-20 rounded-full object-cover" />
          {/* T1: keeps the name column readable on narrow screens; the Compare button moves to the next row instead. */}
          <div className="flex-1 min-w-[12rem]">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Entity title inside the profile card: one step below the page-title size on wide screens. */}
              <h1 className="op-page-title sm:text-3xl sm:leading-9">{creator.name}</h1>
              <PlatformBadge platform={creator.platform} />
              <LiveDemoBadge creator={creator} />
            </div>
            <div className="op-body text-muted-foreground">{creator.handle} • {creator.niche}</div>
          </div>
          <CompareCreatorPicker creatorId={creator.id}>
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="creator-compare-button"><GitCompare className="h-3.5 w-3.5" aria-hidden="true" />Compare with…</Button>
          </CompareCreatorPicker>
          <div className="grid w-full grid-cols-3 gap-4 sm:w-auto">
            <div>
              <div className="op-eyebrow text-muted-foreground">Followers</div>
              <div className="font-mono font-bold text-lg">{creator.followers_available === false ? "Indisponible" : formatNumber(creator.followers)}</div>
            </div>
            <div>
              <div className="op-eyebrow text-muted-foreground">Baseline médiane</div>
              <div className="font-mono font-bold text-lg">{formatNumber(creator.avg_views)}</div>
            </div>
            <div>
              <div className="op-eyebrow text-muted-foreground">Outliers</div>
              <div className="font-mono font-bold text-lg text-[hsl(var(--emerald))]">{outliers.length}</div>
            </div>
          </div>
        </div>
      </Card>

      <p className="op-body text-muted-foreground">{creator.primary_count || 0} auteur principal · {creator.coauthor_count || 0} collaborations attestées · {creator.historical_count || 0} attributions historiques. Statistiques de ce profil, non additives.</p>
      <Card className="p-5">
        <div className="mb-4">
          <h2 className="op-section-title">Video performance</h2>
          <p className="op-meta text-muted-foreground mt-0.5">La ligne indique la médiane des vues positives du profil. Un outlier atteint au moins 2,5× cette référence.</p>
          {/* Videos arrive newest first (published_at desc); the chart keeps the first 20 and plots them oldest to newest. */}
          {(profileTotal ?? videos.length) > chartCount && <p className="op-meta text-muted-foreground mt-1" data-testid="creator-chart-scope">{profileTotal !== null
            ? `Graphique limité aux ${chartCount} vidéos les plus récentes sur ${profileTotal} au total, de la plus ancienne à la plus récente.`
            : `Graphique limité aux ${chartCount} vidéos les plus récentes parmi les ${videos.length} vidéos disponibles (total du profil non communiqué), de la plus ancienne à la plus récente.`} La médiane reste calculée sur l’ensemble du profil.</p>}
        </div>
        <div className="h-64" data-testid="creator-performance-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="idx" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => formatNumber(v)} />
              <Tooltip
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                formatter={(v) => [formatNumber(v), "Views"]}
                labelFormatter={(l) => `Video #${l}`}
              />
              <ReferenceLine y={creator.avg_views} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" />
              <Bar dataKey="views" radius={[4, 4, 0, 0]} fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div>
        <h2 className="op-section-title mb-3">Videos ({videos.length} sur {data.total ?? videos.length})</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
<ReadState read={detailRead} scope="creator-detail" empty={!videos.length} title="Aucune vidéo disponible pour ce créateur" primaryCta={{ label: "Consulter les créateurs", to: "/creators" }}>
          {videos.map((v) => <VideoCard key={v.id} video={v} />)}
          </ReadState>
        </div>
      </div>
    </div>
  );
}
